"use client";

import React, { useMemo } from "react";
import { AISummary } from "@/components/ai/ai-summary";
import { useQuery } from "@apollo/client/react";
import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { useCommunicationsHooks } from "@/lib/hooks/api/communications";
import { useCommunicationTypes } from "@/lib/hooks/api/communications/communication-types";
import AutoImportModal from "@/components/shared/modals/AutoImportModal";
import { getEntityTableConfig } from "@/lib/entity-registry";

const COMMUNICATIONS_CONFIG = getEntityTableConfig<Communications>("communications");
import {
  GetContactssDocument,
  type Communications,
  type GetContactssQuery,
} from "@/src/gql/graphql";
import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import {
  COMPANY_TIMELINE_SUMMARY_OPTIONS,
  buildCompanyScopedCommunicationsFilter,
} from "@/lib/domain/crm-entity-relationships";

interface CompanyActivitiesProps {
  companyId: string;
  companyName: string;
  className?: string;
}

export const CompanyActivities: React.FC<CompanyActivitiesProps> = ({
  companyId,
  companyName,
  className = "",
}) => {
  // Create hooks for activities
  const activityHooks = useCommunicationsHooks();

  // Fetch communication types for the type_id field
  const { options: typeOptions } = useCommunicationTypes();

  // DELIBERATELY includes the company's LINE ANCHOR (`is_company_line`). The
  // anchor is not a person, so it is filtered out of people-shaped surfaces — but
  // this is the COMPANY's timeline, and the anchor is exactly where shared
  // front-desk calls and role-mailbox email are recorded. Excluding it here would
  // hide the company's own shared-line history from the company. Do not add the
  // anchor filter to this query. See CONTACT_METHOD_EXCLUSIVITY.
  const { data: contactsData } = useQuery<GetContactssQuery>(GetContactssDocument, {
    variables: {
      first: 1000,
      filter: {
        company_id: { eq: companyId },
      },
    },
    skip: !companyId,
    errorPolicy: "all",
    fetchPolicy: "cache-first",
  });

  const companyContactIds = useMemo(
    () =>
      (contactsData?.contactsCollection?.edges ?? [])
        .map((edge) => edge?.node?.id)
        .filter((id): id is string => Boolean(id)),
    [contactsData]
  );

  // Update config with dynamic type options and company-specific filters
  const updatedConfig = useMemo(
    () =>
      ({
        ...COMMUNICATIONS_CONFIG,
        fields: {
          ...COMMUNICATIONS_CONFIG.fields,
          type_id: {
            ...COMMUNICATIONS_CONFIG.fields.type_id,
            options: typeOptions,
          },
        },
      }),
    [typeOptions]
  );

  // Memoize custom components to prevent recreation
  // Note: We don't include activityHooks in dependencies because the hooks object
  // reference changes on every render, but the actual hook functions don't change.
  // The AutoImportModal will still have access to the current hooks via closure.
  const customComponents = useMemo(
    () => ({
      importModal: (props: any) => (
        <AutoImportModal config={updatedConfig} hooks={activityHooks} {...props} />
      ),
    }),
    [updatedConfig]
  );

  // Create company-specific custom filters
  const companyCustomFilters = useMemo(
    () => buildCompanyScopedCommunicationsFilter(companyId, companyContactIds),
    [companyContactIds, companyId]
  );

  return (
    <div className={className}>
      <AISummary
        entityType="company"
        entityId={companyId}
        summaryType="timeline"
        options={{ ...COMPANY_TIMELINE_SUMMARY_OPTIONS }}
        showMetadata={true}
        showRefreshButton={true}
        className="mb-6 px-4 sm:px-0"
      />

      <UniversalEntityPage
        entityName="communications"
        config={updatedConfig}
        hooks={activityHooks}
        filterPreset="all"
        customFilters={companyCustomFilters}
        title={`Activities for ${companyName}`}
        description={`View and manage all activities and communications for ${companyName}`}
        storageKey={`company-activities-${companyId}-column-preferences`}
        customComponents={customComponents}
      />
    </div>
  );
};
